import { summarizeBudget, BudgetBreakdown, pct } from "@/components/BudgetViewer";
import { useEffect, useState } from "react";
import { InfoCard } from "./WOGeneralCard";
import { getWOPOCommitments } from "@/lib/api";

// Open-PO dollars by category; refreshes whenever a PO on this WO changes (PurchaseOrders fires "abs:po-changed").
function usePOCommitments(woId) {
  const [committed, setCommitted] = useState(null);
  useEffect(() => {
    if (!woId) { setCommitted(null); return undefined; }
    let alive = true;
    const load = () => getWOPOCommitments(woId).then((r) => { if (alive) setCommitted(r.data?.by_category || null); }).catch(() => {});
    load();
    window.addEventListener("abs:po-changed", load);
    return () => { alive = false; window.removeEventListener("abs:po-changed", load); };
  }, [woId]);
  return committed;
}

// Budget derived from the line items when the WO has no saved budget (labor cost assumed $40/hr).
export function deriveBudget(form) {
  if (form.budget) return form.budget;
  const costTypeMap = { "1": "labor", "2": "material", "3": "equipment", "4": "freight" };
  const b = { material: { cost: 0, revenue: 0 }, labor: { cost: 0, revenue: 0 }, equipment: { cost: 0, revenue: 0 },
              freight: { cost: 0, revenue: 0 }, travel: { cost: 0, revenue: 0 }, disposal: { cost: 0, revenue: 0 }, tax: { cost: 0, revenue: 0 } };
  (form.line_items || []).forEach((item) => {
    const cat = costTypeMap[item.cost_type] || item.cost_type || "material";
    if (!b[cat]) return;
    const t = (item.quantity || 0) * (item.unit_price || 0);
    b[cat].revenue += t;
    if (cat === "labor") b[cat].cost += (item.quantity || 0) * 40;
  });
  return b;
}

// Foundation cost class → breakdown row. Classes the app never budgets (burden, use tax, …) get their own row so
// the table reads 1:1 with Foundation's job cost.
const CLASS_TO_CATEGORY = { "1": "labor", "2": "material", "3": "subcontract", "4": "freight", "5": "equipment",
  "6": "labor_burden", "7": "other", "8": "professional_fees", "9": "training_labor", "10": "use_tax", "U": "use_tax" };
export const ACTUAL_ONLY_LABELS = { labor_burden: "Labor burden", other: "Other", professional_fees: "Professional fees", training_labor: "Training labor", use_tax: "Use tax" };
export function actualsByCategory(snapshot) {
  const byClass = snapshot?.actuals?.cost?.by_class;
  if (!byClass?.length) return null;
  const out = {};
  byClass.forEach((c) => {
    const key = CLASS_TO_CATEGORY[String(c.cost_class_no)] || "other";
    out[key] = Math.round(((out[key] || 0) + (c.cost || 0)) * 100) / 100;
  });
  return out;
}

const Row = ({ label, value, strong, muted, testId, title }) => (
  <div className={`flex items-center justify-between gap-3 text-sm ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`} data-testid={testId} title={title}>
    <span className={strong ? "" : "text-slate-500"}>{label}</span>
    <span className={`num ${muted || ""}`}>{value}</span>
  </div>
);

// Financial summary — its own card, top right beside Directory.
export function WOFinancialSummaryCard({ form, fmt, subtotal, coSubtotal, revisedSubtotal, taxAmount, total, actuals }) {
  const sum = summarizeBudget(deriveBudget(form), form.change_orders);
  const actualCats = actualsByCategory(actuals);
  const actualCost = actualCats ? actuals?.actuals?.cost?.total ?? null : null;
  const actualGM = actualCost != null && revisedSubtotal > 0 ? pct(revisedSubtotal, actualCost) : null;
  return (
    <InfoCard title="Financial Summary" testId="wo-financial-summary-card" className="h-full">
      <div className="space-y-1.5" data-testid="wo-budget-summary">
        <Row label="Base Contract" value={fmt(subtotal)} testId="wo-budget-base" />
        <Row label="Change Orders" value={`${coSubtotal >= 0 ? "+" : ""}${fmt(coSubtotal)}`} testId="wo-budget-cos" />
        <Row label="Current Contract" value={fmt(revisedSubtotal)} strong testId="wo-budget-current" />
        {sum?.hasData && (
          <>
            <Row label="Base GM %" value={`${sum.baseGMPct}%`} testId="wo-budget-base-gm" />
            <Row label="Current GM %" value={`${sum.currentGMPct}%`} testId="wo-budget-current-gm" />
          </>
        )}
        {actualCost != null && (
          <>
            <Row label="Actual Cost" value={fmt(actualCost)} testId="wo-budget-actual-cost" title="Posted job cost from Foundation" />
            <Row label="Actual GM %" value={actualGM == null ? "—" : `${actualGM}%`} testId="wo-budget-actual-gm"
              muted={actualGM != null && actualGM < 0 ? "text-red-600" : "text-emerald-700"} title="(Current contract − Foundation actual cost) ÷ Current contract" />
          </>
        )}
        <Row label="Tax" value={fmt(taxAmount)} testId="wo-budget-tax" />
        <Row label="Total" value={fmt(total)} strong testId="wo-budget-total" />
      </div>
    </InfoCard>
  );
}

// "Budget" card — full width: category table (budget vs Foundation actuals) + the Pull Direct Costs footer.
export function WOBudgetCard({ form, actuals, actualsFooter, woId }) {
  const committed = usePOCommitments(woId);
  const budget = deriveBudget(form);
  const actualCats = actualsByCategory(actuals);
  return (
    <InfoCard title="Budget" testId="wo-budget-card">
      <div data-testid="budget-viewer-section">
        <BudgetBreakdown budget={budget} changeOrders={form.change_orders} actuals={actualCats} committed={committed} extraLabels={ACTUAL_ONLY_LABELS} />
      </div>
      {actualsFooter && <div className="pt-1">{actualsFooter}</div>}
    </InfoCard>
  );
}
